import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";
import { useState } from "react";
import { toast } from "react-toastify";
import { refreshInvoiceVerificationCaches } from "utils/invoiceVerificationQueries";

const CLOSED = { open: false, item: null };

const useInvoiceActivationDecision = ({ onSettled, approveNote } = {}) => {
  const queryClient = useQueryClient();
  const [approveDialog, setApproveDialog] = useState(CLOSED);
  const [rejectDialog, setRejectDialog] = useState(CLOSED);
  const [rejectFeedback, setRejectFeedback] = useState("");

  const settle = async (invoiceId, message) => {
    toast.success(message);
    await Promise.all([onSettled?.(invoiceId), refreshInvoiceVerificationCaches(queryClient)]);
  };

  const approveMutation = useMutation({
    mutationFn: (invoiceId) =>
      axios.post(`${API_URL}/invoice-verification/invoices/${invoiceId}/activation/approve`),
    onSuccess: (_data, invoiceId) => {
      setApproveDialog(CLOSED);
      return settle(invoiceId, "Invoice activated");
    },
    onError: (error) => toast.error(error?.response?.data?.message || "Failed to activate invoice"),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ invoiceId, feedback }) =>
      axios.post(`${API_URL}/invoice-verification/invoices/${invoiceId}/activation/reject`, {
        feedback,
      }),
    onSuccess: (_data, { invoiceId }) => {
      setRejectDialog(CLOSED);
      setRejectFeedback("");
      return settle(invoiceId, "Activation rejected, invoice stays as draft");
    },
    onError: (error) =>
      toast.error(error?.response?.data?.message || "Failed to reject activation"),
  });

  const closeReject = () => {
    setRejectDialog(CLOSED);
    setRejectFeedback("");
  };

  const dialogs = (
    <>
      <Dialog
        open={approveDialog.open}
        onClose={() => setApproveDialog(CLOSED)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Activate Invoice</DialogTitle>
        <DialogContent>
          <Typography>
            Activate {approveDialog.item?.invoiceTitle || "invoice"}
            {approveDialog.item?.clientName ? ` for ${approveDialog.item.clientName}` : ""}? It will
            become emailable.
          </Typography>
          {approveNote && (
            <Typography variant="body2" className="text-app-text-muted" sx={{ mt: 1 }}>
              {approveNote}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(CLOSED)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => approveMutation.mutate(approveDialog.item?.invoiceId)}
            disabled={approveMutation.isLoading}
          >
            {approveMutation.isLoading ? "Activating…" : "Activate"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectDialog.open} onClose={closeReject} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Activation</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason for rejection"
            multiline
            rows={3}
            fullWidth
            required
            value={rejectFeedback}
            onChange={(e) => setRejectFeedback(e.target.value)}
            sx={{ mt: 1 }}
            error={rejectFeedback.trim() === ""}
            helperText={rejectFeedback.trim() === "" ? "Required" : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReject}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={rejectFeedback.trim() === "" || rejectMutation.isLoading}
            onClick={() =>
              rejectMutation.mutate({
                invoiceId: rejectDialog.item?.invoiceId,
                feedback: rejectFeedback.trim(),
              })
            }
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return {
    openApprove: (item) => setApproveDialog({ open: true, item }),
    openReject: (item) => setRejectDialog({ open: true, item }),
    isDeciding: approveMutation.isLoading || rejectMutation.isLoading,
    dialogs,
  };
};

export default useInvoiceActivationDecision;
